import { useCallback, useEffect, useState } from 'react';
import { RemoteImage } from '@cbhq/cds-web/media';
import { Tooltip } from '@cbhq/cds-web/overlays';
import {
  Wallet,
  ConnectWallet,
  WalletDropdownDisconnect,
  WalletDropdown,
} from '@coinbase/onchainkit/wallet';
import { Address, Avatar, Name, Identity, Badge } from '@coinbase/onchainkit/identity';
import { css, cx } from '@emotion/css';
import { useAccount, useChainId, useSwitchChain } from 'wagmi';
import { HStack } from '@cbhq/cds-web/layout';
import { getChainById } from '@/onchain/store/supportedChains';
import { useCompliance } from '@/components/ComplianceProvider/ComplianceProvider';

const AccountNameStyles = css`
  font-size: var(--fontSize-headline);
`;

const AccountConnectButtonStyles = css`
  &,
  & > div,
  & > div > div {
    width: 100%;
    > button {
      width: 100%;
    }
  }
`;

const AccountConnectButtonStandardStyles = css`
  height: 56px;
  border-radius: 56px !important;
  line-height: 1;
  padding-top: 0;
  padding-bottom: 0;
  font-size: var(--fontSize-headline);
  :hover {
    opacity: 0.92 !important;
  }
  :active {
    opacity: 0.86 !important;
  }
  &[data-testid='ockConnectButton'] {
    * {
      fill: var(--color-fgInverse) !important;
      color: var(--color-fgInverse) !important;
      border-color: var(--color-fgInverse) !important;
      border-width: 0;
    }
    background-color: var(--color-bgPrimary) !important;
    width: 100%;
  }
  &[data-testid='ockConnectWallet_Connected'] {
    * {
      fill: var(--color-fg) !important;
      color: var(--color-fg) !important;
      border-color: var(--color-fg) !important;
      border-width: 0;
    }
    .ock-icon-color-inverse {
      fill: white !important;
    }
    background-color: var(--color-bgSecondary) !important;
  }
`;
const NavbarAccountConnectButtonStyles = css`
  height: 40px;
  border-radius: 40px !important;
`;

const ChainIconContainerStyles = css`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
`;

// Fix z-index for badge tooltip to appear above wallet dropdown
const BadgeTooltipStyles = css`
  /* Target the OnchainKit Badge tooltip - use high specificity */
  position: relative;

  /* Badge tooltip should appear above the wallet dropdown */
  [role='tooltip'],
  .ock-identity-badge__tooltip,
  div[class*='tooltip'],
  div[class*='Tooltip'] {
    z-index: 10000 !important;
  }

  /* Also ensure the dropdown itself has proper z-index */
  .ock-wallet-dropdown {
    z-index: 9999;
  }
`;

// Custom hook to check if screen width is less than a threshold
function useIsNarrowScreen(threshold = 415) {
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      setIsNarrow(window.innerWidth <= threshold);
    };

    // Check initially
    checkWidth();

    // Add event listener for resize
    window.addEventListener('resize', checkWidth);

    // Cleanup
    return () => window.removeEventListener('resize', checkWidth);
  }, [threshold]);

  return isNarrow;
}

function AccountWallet({ className }: { className?: string }) {
  const chainId = useChainId();
  const { chainId: accountChainId } = useAccount();
  const { switchChain } = useSwitchChain();
  const handleConnect = useCallback(() => {
    // TODO: Temporary workaround until OnchainKit allows us to specify the chainId on connect()
    // SmartWallet connect is defaulting to chainId = 1 (eth mainnet)
    // This is a problem if the user is on a different chain.
    // This code checks if the user is on eth mainnet and if so, it will switch to the correct chain.
    if (accountChainId === 1 && chainId !== accountChainId) {
      switchChain({ chainId });
    }
  }, [chainId, accountChainId, switchChain]);
  const compliance = useCompliance();

  return (
    <Wallet className={BadgeTooltipStyles}>
      <ConnectWallet className={className} onConnect={handleConnect}>
        {compliance.isVerified && <Badge tooltip="Your connected wallet is verified." />}
        <Avatar
          className={css`
            height: 25px;
            width: 25px;
          `}
        />
        <Name className={AccountNameStyles} />
      </ConnectWallet>
      <WalletDropdown>
        <Identity hasCopyAddressOnClick>
          <Avatar />
          <Name />
          <Address />
        </Identity>

        <WalletDropdownDisconnect />
      </WalletDropdown>
    </Wallet>
  );
}

export function AccountConnectButton() {
  return (
    <HStack className={AccountConnectButtonStyles}>
      <AccountWallet className={AccountConnectButtonStandardStyles} />
    </HStack>
  );
}

export function NavbarAccountConnectButton() {
  const chainId = useChainId();
  const chain = getChainById(chainId.toString());
  const isNarrowScreen = useIsNarrowScreen();

  return (
    <HStack gap={1}>
      {chain && !isNarrowScreen && (
        <Tooltip content={chain.name} testID="navbar-account-connect-button-chain-icon-tooltip">
          <HStack
            background="bgSecondary"
            borderRadius={1000}
            padding={1}
            className={ChainIconContainerStyles}
          >
            <RemoteImage
              testID="navbar-account-connect-button-chain-icon"
              width={24}
              height={24}
              source="/chains/base.svg"
              alt={chain.name}
            />
          </HStack>
        </Tooltip>
      )}
      <AccountWallet
        className={cx(AccountConnectButtonStandardStyles, NavbarAccountConnectButtonStyles)}
      />
    </HStack>
  );
}
